// Panneau de combat : ennemis (icône, barre de PV), héros, round + échéance, mon choix (attaquer une cible, sorts, objets, défense, fuite), qui on attend, journal.
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { isQuietHours } from "../../engine/state";
import type { CombatChoice, Combatant, CombatView, ClientView, ConditionId, Id, SpellDef } from "../../shared/types";
import { send } from "../store";
import { formatDuration, useLiveNow } from "./SollicitationCard";
import "./CombatPanel.css";

export interface CombatPanelProps { view: ClientView }

const CONDITION_LABEL: Record<ConditionId, string> = {
  empoisonne: "☠️ Empoisonné", saignement: "🩸 Saigne", etourdi: "💫 Étourdi", beni: "✨ Béni",
  affame: "🍂 Affamé", famine: "🥀 Famine", epuise: "😮‍💨 Épuisé", invisible: "🫥 Invisible",
  protege: "🛡️ Protégé", enflamme: "🔥 Enflammé", gele: "❄️ Gelé", maudit: "🌑 Maudit",
  inspire: "🌟 Inspiré", cicatrice_oubli: "🩹 Cicatrice d’oubli", repose: "😌 Reposé",
};

/** Délai de sécurité : si le serveur ne répond pas, les boutons se rallument. */
const SENDING_TIMEOUT_MS = 8_000;

type Step =
  | { kind: "menu" }
  | { kind: "attaque" }
  | { kind: "sort" }
  | { kind: "sort-cible"; spell: SpellDef }
  | { kind: "objet" }
  | { kind: "objet-cible"; iid: Id }
  | { kind: "fuite" };

interface Hit { n: number; delta: number }

export function CombatPanel(props: CombatPanelProps) {
  const { view } = props;
  const combat = view.combat;
  if (!combat) return null;
  return <CombatBody view={view} combat={combat} />;
}

function CombatBody({ view, combat }: { view: ClientView; combat: CombatView }) {
  const live = useLiveNow(view.now);
  const character = view.character;
  const [step, setStep] = useState<Step>({ kind: "menu" });
  const [sending, setSending] = useState<CombatChoice | null>(null);
  const sendTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const combatants = combat.combatants;
  const hits = useHits(combatants);
  const enemies = combatants.filter((c) => c.side === "ennemis");
  const heroes = combatants.filter((c) => c.side === "heros");
  const me = combat.myCid ? combatants.find((c) => c.cid === combat.myCid) ?? null : null;
  const alive = isStanding;
  const livingEnemies = enemies.filter(alive);
  const livingHeroes = heroes.filter(alive);
  const canAct = combat.status === "en_cours" && me !== null && alive(me) && combat.myChoice === null;
  const ongoing = combat.status === "en_cours";

  // Le round change : on repart du menu, et l'envoi en attente est oublié.
  useEffect(() => {
    setStep({ kind: "menu" });
    setSending(null);
  }, [combat.id, combat.round]);

  // Le serveur a confirmé mon choix : l'envoi en attente n'a plus lieu d'être.
  useEffect(() => {
    if (combat.myChoice !== null) {
      setSending(null);
      if (sendTimer.current !== null) clearTimeout(sendTimer.current);
    }
  }, [combat.myChoice]);

  useEffect(() => () => {
    if (sendTimer.current !== null) clearTimeout(sendTimer.current);
  }, []);

  function commit(choice: CombatChoice) {
    if (sending !== null || !canAct) return;
    setSending(choice);
    if (sendTimer.current !== null) clearTimeout(sendTimer.current);
    sendTimer.current = setTimeout(() => setSending(null), SENDING_TIMEOUT_MS);
    send({ t: "combat_choice", choice });
    setStep({ kind: "menu" });
  }

  const locked = sending !== null || !canAct;
  const shownChoice = combat.myChoice ?? sending;
  const castable = (character?.spells ?? []).filter((s) => s.kind !== "utilitaire");
  const consumables = (character?.inventory ?? []).filter((it) => it.def.kind === "consommable" && it.def.use !== undefined);

  const deadlineLeft = combat.roundDeadline - live;
  const quiet = ongoing && isQuietHours(live);

  return (
    <section class="combat-panel" aria-label="Combat en cours">
      <header class="combat-panel-banner">
        <h2 class="combat-panel-title">
          <span aria-hidden="true">⚔️</span> Combat — round {combat.round}
        </h2>
        {ongoing ? (
          <p class="combat-panel-deadline">
            {quiet
              ? "Heures calmes : le round attend 8 h."
              : deadlineLeft <= 0
                ? "Le round se résout…"
                : `Fin du round dans ${formatDuration(deadlineLeft)}${combat.myChoice === null ? " — sans choix, tu te défendras." : "."}`}
          </p>
        ) : (
          <p class="combat-panel-deadline combat-panel-deadline--end">{outcomeText(combat.status)}</p>
        )}
      </header>

      <div class="combat-panel-section">
        <h3 class="combat-panel-section-title">Ennemis</h3>
        <ul class="combat-panel-foes" role="list">
          {enemies.map((c) => (
            <li key={c.cid}>
              <Foe
                c={c}
                targetable={alive(c) && (step.kind === "attaque" || (step.kind === "sort-cible" && step.spell.target === "ennemi"))}
                hit={hits[c.cid]}
                onPick={() => {
                  if (step.kind === "attaque") commit({ kind: "attaque", targetCid: c.cid });
                  else if (step.kind === "sort-cible") commit({ kind: "sort", spellKey: step.spell.key, targetCid: c.cid });
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      <div class="combat-panel-section">
        <h3 class="combat-panel-section-title">Héros</h3>
        <ul class="combat-panel-heroes" role="list">
          {heroes.map((c) => (
            <li key={c.cid}>
              <Hero
                c={c}
                isMe={c.cid === combat.myCid}
                targetable={alive(c) && (step.kind === "objet-cible" || (step.kind === "sort-cible" && step.spell.target === "allie"))}
                hit={hits[c.cid]}
                onPick={() => {
                  if (step.kind === "objet-cible") commit({ kind: "objet", iid: step.iid, targetCid: c.cid });
                  else if (step.kind === "sort-cible") commit({ kind: "sort", spellKey: step.spell.key, targetCid: c.cid });
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      <section class="combat-panel-choice" aria-label="Ton choix">
        {!ongoing ? null : shownChoice !== null ? (
          <p class="combat-panel-choice-text" aria-live="polite">
            Ton choix : {describeChoice(shownChoice, combatants, castable, character?.inventory ?? [])}
            {sending !== null && combat.myChoice === null ? " (envoi…)" : ""}
            {" — "}
            {combat.waitingFor.length > 0
              ? <>en attente de : {combat.waitingFor.join(", ")}</>
              : <>tous ont choisi, le round se résout.</>}
          </p>
        ) : combat.myCid === null ? (
          <p class="combat-panel-choice-text">Tu observes le combat.</p>
        ) : !ongoing ? null : !me || !alive(me) ? (
          <p class="combat-panel-choice-text">
            {me?.fled ? "Tu as fui le combat." : "Tu es à terre : tu ne peux plus agir ce round."}
          </p>
        ) : (
          <ActionMenu
            step={step}
            setStep={setStep}
            locked={locked}
            livingEnemies={livingEnemies}
            livingHeroes={livingHeroes}
            myCid={combat.myCid}
            castable={castable}
            consumables={consumables}
            mana={character?.mana ?? 0}
            commit={commit}
          />
        )}
      </section>

      <details class="combat-panel-log">
        <summary>Journal du round</summary>
        {combat.log.length === 0 ? (
          <p class="combat-panel-log-empty">Rien à signaler pour l’instant.</p>
        ) : (
          <ol class="combat-panel-log-list">
            {combat.log.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
        )}
      </details>
    </section>
  );
}

interface ActionMenuProps {
  step: Step;
  setStep: (s: Step) => void;
  locked: boolean;
  livingEnemies: Combatant[];
  livingHeroes: Combatant[];
  myCid: Id | null;
  castable: SpellDef[];
  consumables: { iid: Id; qty: number; def: { name: string; icon: string } }[];
  mana: number;
  commit: (choice: CombatChoice) => void;
}

function ActionMenu(p: ActionMenuProps) {
  const { step } = p;
  const back = () => p.setStep({ kind: "menu" });

  if (step.kind === "menu") {
    return (
      <div class="combat-panel-actions" role="group" aria-label="Choisis ton action">
        <button type="button" class="combat-panel-action" disabled={p.locked} onClick={() => p.setStep({ kind: "attaque" })}>
          <span aria-hidden="true">⚔️</span> Attaquer
        </button>
        <button type="button" class="combat-panel-action" disabled={p.locked} onClick={() => p.setStep({ kind: "sort" })}>
          <span aria-hidden="true">✨</span> Sort
        </button>
        <button type="button" class="combat-panel-action" disabled={p.locked} onClick={() => p.setStep({ kind: "objet" })}>
          <span aria-hidden="true">🧪</span> Objet
        </button>
        <button type="button" class="combat-panel-action" disabled={p.locked} onClick={() => p.commit({ kind: "defense" })}>
          <span aria-hidden="true">🛡️</span> Se défendre
        </button>
        <button type="button" class="combat-panel-action combat-panel-action--flee" disabled={p.locked} onClick={() => p.setStep({ kind: "fuite" })}>
          <span aria-hidden="true">🏃</span> Fuir
        </button>
      </div>
    );
  }

  if (step.kind === "attaque") {
    return (
      <div class="combat-panel-sub">
        <p class="combat-panel-hint">Choisis la cible de ton attaque.</p>
        {p.livingEnemies.length === 0 ? <p class="combat-panel-hint">Aucun ennemi debout.</p> : null}
        <button type="button" class="btn-ghost combat-panel-back" onClick={back}>← Retour</button>
      </div>
    );
  }

  if (step.kind === "sort") {
    return (
      <div class="combat-panel-sub">
        <p class="combat-panel-hint">Quel sort lances-tu ?</p>
        {p.castable.length === 0 ? <p class="combat-panel-hint">Aucun sort utilisable en combat.</p> : null}
        <ul class="combat-panel-list" role="list">
          {p.castable.map((s) => {
            const tooCostly = p.mana < s.mana;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  class="combat-panel-option"
                  disabled={p.locked || tooCostly}
                  onClick={() => {
                    if (s.target === "ennemi" || s.target === "allie") p.setStep({ kind: "sort-cible", spell: s });
                    else if (s.target === "soi") p.commit({ kind: "sort", spellKey: s.key, targetCid: p.myCid });
                    else p.commit({ kind: "sort", spellKey: s.key, targetCid: null });
                  }}
                >
                  <span aria-hidden="true">{s.icon}</span>
                  <span class="combat-panel-option-name">{s.name}</span>
                  <span class="combat-panel-option-cost">{s.mana} mana</span>
                </button>
              </li>
            );
          })}
        </ul>
        <button type="button" class="btn-ghost combat-panel-back" onClick={back}>← Retour</button>
      </div>
    );
  }

  if (step.kind === "sort-cible") {
    const onAllies = step.spell.target === "allie";
    const targets = onAllies ? p.livingHeroes : p.livingEnemies;
    return (
      <div class="combat-panel-sub">
        <p class="combat-panel-hint">
          {step.spell.icon} {step.spell.name} : {onAllies ? "sur qui ?" : "sur quel ennemi ?"}
        </p>
        {targets.length === 0 ? <p class="combat-panel-hint">Personne à viser.</p> : null}
        <button type="button" class="btn-ghost combat-panel-back" onClick={() => p.setStep({ kind: "sort" })}>← Retour</button>
      </div>
    );
  }

  if (step.kind === "objet") {
    return (
      <div class="combat-panel-sub">
        <p class="combat-panel-hint">Quel objet utilises-tu ?</p>
        {p.consumables.length === 0 ? <p class="combat-panel-hint">Tu n’as aucun consommable.</p> : null}
        <ul class="combat-panel-list" role="list">
          {p.consumables.map((it) => (
            <li key={it.iid}>
              <button type="button" class="combat-panel-option" disabled={p.locked} onClick={() => p.setStep({ kind: "objet-cible", iid: it.iid })}>
                <span aria-hidden="true">{it.def.icon}</span>
                <span class="combat-panel-option-name">{it.def.name}</span>
                <span class="combat-panel-option-cost">× {it.qty}</span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" class="btn-ghost combat-panel-back" onClick={back}>← Retour</button>
      </div>
    );
  }

  if (step.kind === "objet-cible") {
    return (
      <div class="combat-panel-sub">
        <p class="combat-panel-hint">Sur qui l’utilises-tu ? Choisis dans la liste des héros ci-dessus.</p>
        <button type="button" class="btn-ghost combat-panel-back" onClick={() => p.setStep({ kind: "objet" })}>← Retour</button>
      </div>
    );
  }

  // fuite : confirmation, la fuite se tente par un jet d'Acrobaties
  return (
    <div class="combat-panel-sub">
      <p class="combat-panel-hint">Tu tentes de t’échapper par un jet d’Acrobaties. Confirmer ?</p>
      <div class="combat-panel-confirm">
        <button type="button" class="btn-danger" disabled={p.locked} onClick={() => p.commit({ kind: "fuite" })}>
          Oui, fuir
        </button>
        <button type="button" class="btn-ghost" onClick={back}>Rester</button>
      </div>
    </div>
  );
}

function outcomeText(status: CombatView["status"]): string {
  if (status === "victoire") return "Victoire ! Le combat est gagné.";
  if (status === "defaite") return "Défaite… le combat est perdu.";
  if (status === "fuite") return "Vous avez pris la fuite.";
  return "";
}

function describeChoice(choice: CombatChoice, combatants: Combatant[], spells: SpellDef[], inventory: { iid: Id; def: { name: string } }[]): string {
  const nameOf = (cid: Id | null | undefined) => combatants.find((c) => c.cid === cid)?.name ?? "";
  switch (choice.kind) {
    case "attaque":
      return `attaquer ${nameOf(choice.targetCid)}`;
    case "sort": {
      const spell = spells.find((s) => s.key === choice.spellKey);
      const label = spell?.name ?? "un sort";
      const target = choice.targetCid ? nameOf(choice.targetCid) : "";
      return target ? `${label} sur ${target}` : label;
    }
    case "objet": {
      const item = inventory.find((it) => it.iid === choice.iid);
      const target = choice.targetCid ? nameOf(choice.targetCid) : "";
      const label = item ? item.def.name : "un objet";
      return target ? `${label} sur ${target}` : label;
    }
    case "defense":
      return "te défendre";
    case "fuite":
      return "fuir";
  }
}

function hpPct(c: Combatant): number {
  if (c.hpMax <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((c.hp / c.hpMax) * 100)));
}

/** Secousse et chiffre flottant quand les PV changent (comparaison avec la vue précédente). */
function useHits(combatants: Combatant[]): Record<Id, Hit> {
  const prev = useRef<Map<Id, number>>(new Map());
  const [hits, setHits] = useState<Record<Id, Hit>>({});
  useEffect(() => {
    const before = prev.current;
    const after = new Map<Id, number>();
    const changed: [Id, number][] = [];
    for (const c of combatants) {
      after.set(c.cid, c.hp);
      const was = before.get(c.cid);
      if (was !== undefined && was !== c.hp) changed.push([c.cid, c.hp - was]);
    }
    prev.current = after;
    if (changed.length === 0) return;
    setHits((h) => {
      const next = { ...h };
      for (const [cid, delta] of changed) next[cid] = { n: (h[cid]?.n ?? 0) + 1, delta };
      return next;
    });
  }, [combatants]);
  return hits;
}

interface CombatantProps {
  c: Combatant;
  targetable: boolean;
  hit?: Hit;
  onPick?: () => void;
}

function Foe({ c, targetable, hit, onPick }: CombatantProps) {
  const down = !isStanding(c);
  const pct = hpPct(c);
  const body = (
    <>
      <span class="combat-panel-foe-icon" aria-hidden="true">{c.icon}</span>
      <span class="combat-panel-foe-body">
        <span class="combat-panel-foe-name">{c.name}</span>
        <span class="combat-panel-foe-stats">
          <span class="combat-panel-chip">CA {c.ca}</span>
          <span class="combat-panel-chip">PV {Math.max(0, c.hp)}/{c.hpMax}</span>
          {c.fled ? <span class="combat-panel-chip">A fui</span> : null}
          {!c.fled && c.hp <= 0 ? <span class="combat-panel-chip">Vaincu</span> : null}
        </span>
        <span class="bar bar-hp combat-panel-bar" role="meter" aria-label={`PV de ${c.name}`} aria-valuemin={0} aria-valuemax={c.hpMax} aria-valuenow={Math.max(0, c.hp)}>
          <span class="bar-fill" style={{ width: `${pct}%` }} />
        </span>
        {c.conditions.length > 0 ? (
          <span class="combat-panel-conds">
            {c.conditions.map((k, i) => (
              <span key={`${k.id}-${i}`} class="combat-panel-cond">{CONDITION_LABEL[k.id]}</span>
            ))}
          </span>
        ) : null}
      </span>
    </>
  );
  const cls = [
    "combat-panel-foe",
    down ? "combat-panel-foe--down" : "",
    targetable ? "combat-panel-foe--target" : "",
  ].filter(Boolean).join(" ");
  const inner = <HitBox hit={hit}>{body}</HitBox>;
  return targetable && onPick ? (
    <button type="button" class={cls} onClick={onPick} aria-label={`Viser ${c.name}`}>{inner}</button>
  ) : (
    <div class={cls}>{inner}</div>
  );
}

function Hero({ c, isMe, targetable, hit, onPick }: CombatantProps & { isMe: boolean }) {
  const down = !isStanding(c);
  const pct = hpPct(c);
  const body = (
    <>
      <span class="combat-panel-hero-icon" aria-hidden="true">{c.icon}</span>
      <span class="combat-panel-hero-name">{isMe ? `${c.name} (toi)` : c.name}</span>
      <span class="bar bar-hp combat-panel-bar combat-panel-bar--small" role="meter" aria-label={`PV de ${c.name}`} aria-valuemin={0} aria-valuemax={c.hpMax} aria-valuenow={Math.max(0, c.hp)}>
        <span class="bar-fill" style={{ width: `${pct}%` }} />
      </span>
      <span class="combat-panel-hero-hp">{Math.max(0, c.hp)}/{c.hpMax}</span>
      {c.defending && !down ? <span class="combat-panel-chip">🛡️ Défense</span> : null}
      {c.fled ? <span class="combat-panel-chip">Fuite</span> : null}
      {!c.fled && c.hp <= 0 ? <span class="combat-panel-chip">À terre</span> : null}
    </>
  );
  const cls = [
    "combat-panel-hero",
    isMe ? "combat-panel-hero--me" : "",
    down ? "combat-panel-hero--down" : "",
    targetable ? "combat-panel-hero--target" : "",
  ].filter(Boolean).join(" ");
  const inner = <HitBox hit={hit}>{body}</HitBox>;
  return targetable && onPick ? (
    <button type="button" class={cls} onClick={onPick} aria-label={`Choisir ${c.name}`}>{inner}</button>
  ) : (
    <div class={cls}>{inner}</div>
  );
}

function HitFloat({ hit }: { hit: Hit }) {
  if (hit.delta === 0) return null;
  const up = hit.delta > 0;
  return (
    <span class={up ? "combat-panel-float combat-panel-float--up" : "combat-panel-float combat-panel-float--down"} aria-hidden="true">
      {up ? `+${hit.delta}` : `${hit.delta}`}
    </span>
  );
}

function isStanding(c: Combatant): boolean {
  return c.hp > 0 && !c.fled;
}

/** Cadre d'une carte : rejoue la secousse ou le chiffre flottant à chaque nouveau coup. */
function HitBox({ hit, children }: { hit?: Hit; children: ComponentChildren }) {
  return (
    <span key={hit?.n ?? 0} class={hit && hit.delta < 0 ? "combat-panel-hit combat-panel-hit--shake" : "combat-panel-hit"}>
      {children}
      {hit ? <HitFloat hit={hit} /> : null}
    </span>
  );
}
